/* eslint-disable react/prop-types */
import React, { useState, useCallback } from 'react';
import { Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { useMutation, useQuery } from '@apollo/client';
import { Elements } from '@stripe/react-stripe-js';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import { C, F } from '../constants/theme';
import {
  Btn,
  Brandmark,
  Callout,
  Heading,
  Sub,
  Tag,
  Fade,
  Input,
} from '../components';
import PaymentFormInner from '../components/PaymentFormInner';
import { FormGroup, Checkbox, SanitizedHTML } from 'core/ui/atoms';
import { goToWeeklyMenu } from 'core/api/actions/navigation';
import { formatPhoneNumber } from 'core/api/utils';
import useUser from 'core/hooks/useUser';
import { useProgramContext } from 'core/api/programContext';
import { SET_ACCOUNT_CREDENTIALS, SET_DELIVERY_INFO, SET_SMS_PREFERENCES, COMPLETE_SPLIT_TEST_ORDER_PURCHASE } from 'core/api/apollo/mutations';
import { GET_PREPAID_PURCHASE, GET_DELIVERY_DAY, GET_ORDER, GET_SPLIT_TEST_ORDER, GET_USER_HEALTH_GOALS, GET_USER_BLOODWORK_MARKERS, LIST_HEALTH_GOALS } from 'core/api/apollo/queries';
import stripePromise from 'core/api/stripe';
import BackgroundJobStatusCheck from 'core/ui/components/BackgroundJobStatusCheck';
import client from 'core/api/apollo/apolloClient';
import { accessChecksDisabledVar } from 'core/api/apollo/cache';
import Tracker from 'core/api/tracking/tracker';
import { US_STATES } from 'core/api/constants';
import { InputGroupLabel } from 'core/ui/atoms/V2';

const Phase8Checkout = ({
  checkoutStep,
  scrollTop,
  setMode,
  goPhase,
}) => {
  const {
    selectedProgram,
    programSize,
    selectedDiet,
    oneTimeProgramId,
    bundleLabel,
    daysLabel,
    specs,
    goals,
  } = useProgramContext();
  const PosPurchaseVideoURL = 'https://d1cbtwb0qnacuk.cloudfront.net/videos/PostPurchaseWelcomeVideo_Methodology_Origins_Final_16x9.mp4';
  const { currentUser } = useUser();
  const { primaryAddress } = currentUser;
  const states = US_STATES.map((s) => ({ title: s.name, value: s.abbreviation }));
  const progLabel = selectedProgram === 'sustain' ? 'Sustain GLP-1' : selectedProgram === 'peak' ? 'Peak' : 'Signature';
  const sizeLabel = programSize?.name || 'Standard';

  const [dietryRestriction, setDietryRestriction] = useState(false);
  const [timingRestriction, setTimingRestriction] = useState(false);

  const [password, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Address States
  const [streetAddress, setStreetAddress] = useState(primaryAddress?.street1 || '');
  const [apartment, setApartment] = useState(primaryAddress?.street2 || '');
  const [city, setCity] = useState(primaryAddress?.city || '');
  const state = currentUser?.primaryAddress?.state;
  const zip = currentUser?.primaryAddress?.zipCode;
  const [phoneNumber, setPhoneNumber] = useState(currentUser?.phoneNumber || '');
  const [instructions, setInstructions] = useState(primaryAddress?.instructions || '');
  const [receiveReminders, setReceiveReminders] = useState(false);
  const [receiveNewsletter, setReceiveNewsletter] = useState(false);

  // Purchase completion states
  const [backgroundJobId, setBackgroundJobId] = useState(null);
  const [isProcessingPurchase, setIsProcessingPurchase] = useState(false);
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [assignedOrderId, setAssignedOrderId] = useState(null);

  // Fetch purchase data directly when on the welcome screen so it's self-sufficient
  // regardless of whether purchaseSummary state survived the store reset
  const { data: welcomePurchaseData } = useQuery(GET_PREPAID_PURCHASE, {
    skip: checkoutStep !== 3,
    fetchPolicy: 'network-only',
  });
  const effectivePurchase = welcomePurchaseData?.getPrepaidPurchase || purchaseSummary;

  // After purchase, poll the first delivery day until meal assignment creates an order
  const firstDeliveryDayId = effectivePurchase?.deliveryPeriods?.[0]?.days?.[0]?.id;
  useQuery(GET_DELIVERY_DAY, {
    variables: { deliveryDayId: firstDeliveryDayId },
    skip: !firstDeliveryDayId || !!assignedOrderId,
    fetchPolicy: 'network-only',
    pollInterval: 4000,
    onCompleted: (data) => {
      const orderId = data?.getDeliveryDay?.order?.id;
      if (orderId) setAssignedOrderId(orderId);
    },
  });

  const { data: orderData } = useQuery(GET_ORDER, {
    variables: { orderId: assignedOrderId },
    skip: !assignedOrderId,
    fetchPolicy: 'network-only',
  });

  const assignedTopMeals = (orderData?.getOrder?.lineItems || [])
    .map((li) => li.product?.name)
    .filter(Boolean)
    .slice(0, 3);

  const { data: splitTestData } = useQuery(GET_SPLIT_TEST_ORDER, {
    variables: { oneTimeProgramId },
    fetchPolicy: 'cache-first',
    skip: !oneTimeProgramId,
  });
  const splitTestMeals = (splitTestData?.getSplitTestOrder || [])
    .slice(0, 3)
    .map((m) => m.productVariantName)
    .filter(Boolean);

  const { data: healthGoalsData } = useQuery(GET_USER_HEALTH_GOALS, {
    skip: checkoutStep !== 3,
    fetchPolicy: 'network-only',
  });
  const userHealthGoals = healthGoalsData?.currentUser?.healthProfile?.healthGoals || [];

  const { data: { listHealthGoals = [] } = {} } = useQuery(LIST_HEALTH_GOALS);

  const { data: { listUserBloodworkMarkers = [] } = {} } = useQuery(GET_USER_BLOODWORK_MARKERS, {
    skip: checkoutStep !== 3,
    fetchPolicy: 'network-only',
  });

  const isFormFilled = !isEmpty(streetAddress) && !isEmpty(city) && !isEmpty(phoneNumber) && !isEmpty(instructions);

  const splitTestOrderId = splitTestData?.getSplitTestOrder?.[0]?.orderId;

  const [completeSplitTestOrderPurchaseMutation] = useMutation(COMPLETE_SPLIT_TEST_ORDER_PURCHASE);

  const handlePaymentComplete = useCallback(async () => {
    setIsProcessingPurchase(true);
    try {
      const { data } = await completeSplitTestOrderPurchaseMutation({
        variables: { input: { orderId: splitTestOrderId } },
      });
      const { completeSplitTestOrderPurchase: { backgroundJob } } = data;
      setBackgroundJobId(backgroundJob.id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to complete split test order purchase:', error);
      setIsProcessingPurchase(false);
    }
  }, [completeSplitTestOrderPurchaseMutation, splitTestOrderId]);

  const handlePurchaseSuccess = useCallback(async () => {
    try {
      const { data } = await client.query({ query: GET_PREPAID_PURCHASE, fetchPolicy: 'network-only' });
      const prepaidPurchase = data?.getPrepaidPurchase;
      if (prepaidPurchase) {
        setPurchaseSummary(prepaidPurchase);
      }
      if (prepaidPurchase && prepaidPurchase.paymentId) {
        Tracker.trackMetaPurchase({
          currency: 'USD',
          value: prepaidPurchase.total.amount * 0.01,
          userData: currentUser,
          eventTime: Math.floor(Date.now() / 1000),
          actionSource: 'website',
          data_processing_options: [],
          external_id: currentUser.externalId,
        }, {
          eventID: `purchase-${prepaidPurchase.paymentId}`,
        });
        Tracker.trackGoogleAdManagerPurchase({
          transactionId: prepaidPurchase.paymentId,
          purchaseAmount: prepaidPurchase.total.amount * 0.01,
          userData: currentUser,
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch purchase data for tracking:', error);
    }
    accessChecksDisabledVar(true);
    client.stop();
    client.resetStore().then(() => {
      accessChecksDisabledVar(false);
      setIsProcessingPurchase(false);
      setBackgroundJobId(null);
      goPhase(8, 3);
    });
  }, [currentUser, goPhase]);

  const handlePurchaseError = useCallback((errors) => {
    setIsProcessingPurchase(false);
    setBackgroundJobId(null);
    // eslint-disable-next-line no-console
    console.error('Purchase processing failed:', errors);
  }, []);

  const [setPassword] = useMutation(SET_ACCOUNT_CREDENTIALS, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('password set successfully:', data);
      goPhase(8, 1);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error in setting password:', error);
    },
  });

  const isPasswordValid = password.length >= 8 && password.length <= 128 && password === confirmPassword;

  const handleSetPassword = async () => {
    if (!isPasswordValid) return;
    try {
      await setPassword({
        variables: {
          input: {
            password: password,
            passwordConfirmation: confirmPassword,
            email: currentUser?.email,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set password:', error);
      throw error;
    }
  };

  const [setDeliveryInfo] = useMutation(SET_DELIVERY_INFO, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('address set successfully:', data);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error in setting address:', error);
    },
  });

  const handleSetAddress = async () => {
    try {
      await setDeliveryInfo({
        variables: {
          input: {
            street1: streetAddress,
            street2: apartment,
            city: city,
            phoneNumber: phoneNumber,
            zipcode: zip,
            instructions: instructions,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set password:', error);
      throw error;
    }
  };

  const [setSmsPreferences] = useMutation(SET_SMS_PREFERENCES, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('SMS preferences set successfully', data);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error in setting SMS preferences:', error);
    },
  });

  const handleSmsPreference = async () => {
    try {
      await setSmsPreferences({
        variables: {
          input: {
            receiveReminders: receiveReminders,
            receiveNewsletter: receiveNewsletter,
          },
        },
      });
    } catch (error) {
      console.error('Failed to set password:', error);
      throw error;
    }
  };

  const handleContinueToPayment = async () => {
    try {
      await Promise.all([handleSetAddress(), handleSmsPreference()]);
      goPhase(8, 2);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save delivery info or SMS preferences:', error);
    }
  };

  const name = currentUser?.firstName || '';
  const eatStyle = selectedDiet?.name || null;
  const selectedState = states.find((s) => s.value === state) || null;

  return (
    <div style={{ fontFamily: F.b, color: C.darkBrown, background: C.white, minHeight: '100vh', overflowY: 'auto' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '28px 20px 48px' }}>
        <Fade key={checkoutStep}>
          {checkoutStep === 0 && <>
            <Heading size={26}>Let's lock this in, {name || 'there'}.</Heading>
            <Sub>First, create your account. You'll use this to manage your menu each week.</Sub>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              <Input placeholder="you@example.com" value={currentUser?.email} onChange={() => {}} />
              <Input placeholder="Create a password" type="password" value={password} onChange={setNewPassword}/>
              <Input placeholder="Confirm password" type="password" value={confirmPassword} onChange={setConfirmPassword}/>
              <div style={{ fontSize: 11, color: C.lightBrown }}>Minimum 8 characters.</div>
              {password.length > 0 && password.length < 8 && (
                <div style={{ fontSize: 11, color: '#c0392b' }}>Password must be at least 8 characters.</div>
              )}
              {confirmPassword.length > 0 && password !== confirmPassword && (
                <div style={{ fontSize: 11, color: '#c0392b' }}>Passwords do not match.</div>
              )}
            </div>
            <Callout>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.darkBrown, marginBottom: 6 }}>Plan Summary</div>
              {[`Program: ${progLabel} · ${sizeLabel}`, `Nutrition: ${specs.prot} protein · ${specs.cal} cal per meal`, `Meals: ${bundleLabel}`, `${daysLabel}`, `Goals: ${goals.map((g) => listHealthGoals.find((x) => x.id === g)?.name).filter(Boolean).join(', ')}`, `Eating Style: ${eatStyle || '—'}`].map((t, i) => <div key={i} style={{ fontSize: 12, color: C.brown, marginBottom: 3 }}><SanitizedHTML rawHTML={t} /></div>)}
            </Callout>
            <CustomFormGroup role="checkbox" aria-labelledby="dietaryRestrictions">
              <InputGroupLabel id='dietaryRestrictions'>Order and Dietary Restrictions</InputGroupLabel>
              <CustomCheckbox
                label="I acknowledge that Methodology cannot accommodate food allergies or specific dietary restrictions. I understand that all meals are free of gluten and dairy. I also acknowledge that I may swap as many meals as I'd like each week directly through the Methodology website."
                name="dietryRestriction"
                value="dietryRestriction"
                onChange={() => setDietryRestriction((value) => !value)}
                checked={dietryRestriction}
              />
            </CustomFormGroup>
            <CustomFormGroup>
              <CustomCheckbox
                label={
                  <span>
                    I acknowledge that meal swaps must be completed through the Methodology website by Sunday at 11:00 PM PT, one week prior to my scheduled delivery. Postponement requests may be made on the website by Friday at 6:00 AM PT (10 days before delivery). After this deadline, but before Sunday at 11:00 PM PT (8 days before delivery), I may request assistance by emailing <InnerLink href="mailto:help@gomethodology.com"> help@gomethodology.com</InnerLink>. Requests made beyond these timeframes cannot be accommodated.
                  </span>
                }
                name="timingRestriction"
                value="timingRestriction"
                onChange={() => setTimingRestriction((value) => !value)}
                checked={timingRestriction}
              />
            </CustomFormGroup>
            <Btn
              onClick={handleSetPassword}
              disabled={!timingRestriction || !dietryRestriction || !isPasswordValid}
              style={{ width: '100%' }}>Continue to Shipping <ChevronRight size={15}/>
            </Btn>
          </>}
          {checkoutStep === 1 && <>
            <Heading size={26}>Where are we delivering?</Heading>
            <Sub>We'll show pricing once we know your area.</Sub>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
              <Input placeholder="Street address" value={streetAddress} onChange={setStreetAddress}/>
              <Input placeholder="Apt, suite, unit (optional)" value={apartment} onChange={setApartment}/>
              <div style={{ display: 'flex', gap: 10 }}>
                <Input placeholder="City" disabled={true} value={city} onChange={setCity}/>
                <Input placeholder="State" disabled={true} value={selectedState?.title || ''} onChange={() => {}}/>
                <Input placeholder={zip || 'Zip'} disabled={true} value={zip || ''} onChange={() => {}} />
              </div>
              <Input
                placeholder="Mobile number"
                // value={phoneNumber}
                value={formatPhoneNumber(phoneNumber || '')}
                onChange={(value) => setPhoneNumber(value.replace(/\D/g, ''))}
              />
              <div style={{ fontSize: 11, color: C.lightBrown, marginBottom: 4 }}>
                Used for delivery alerts and urgent order updates.
              </div>
            </div>

            {/* Delivery Instructions */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 6 }}>
                Overnight Courier Delivery Instructions
              </div>
              <div style={{ fontSize: 11, color: C.lightBrown, lineHeight: 1.5, marginBottom: 8 }}>
                Help couriers find your home in the dark and place your box in the right place. If possible, include a backup option (eg, if the lobby is locked or the gate code doesn't work).
              </div>
              <textarea
                placeholder={"eg. The gate code is: 123#. You're at the right house if you see lawn gnomes. Leave box on porch behind potted plant by front door."}
                rows={4}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                style={{ width: '100%', padding: '12px 14px', border: '1px solid #E8E4DC', fontFamily: F.b, fontSize: 13, color: C.darkBrown, background: C.pureWhite, outline: 'none', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5, minHeight: 90 }}
              />
            </div>

            {/* SMS Preferences */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              <label
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
                onClick={() => setReceiveReminders(!receiveReminders)}>
                <div style={{ width: 18, height: 18, border: `1.5px solid ${receiveReminders ? C.darkBrown : '#E8E4DC'}`, background: receiveReminders ? C.darkBrown : C.pureWhite, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  {receiveReminders && <Check size={12} color={C.pureWhite} strokeWidth={3}/>}
                </div>
                <span style={{ fontSize: 12, color: C.brown, lineHeight: 1.4 }}>
                  Send me SMS reminders for orders, deadlines, and deliveries.
                </span>
              </label>
              <label
                style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}
                onClick={() => setReceiveNewsletter(!receiveNewsletter)}>
                <div style={{ width: 18, height: 18, border: `1.5px solid ${receiveNewsletter ? C.darkBrown : '#E8E4DC'}`, background: receiveNewsletter ? C.darkBrown : C.pureWhite, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  {receiveNewsletter && <Check size={12} color={C.pureWhite} strokeWidth={3}/>}
                </div>
                <span style={{ fontSize: 12, color: C.brown, lineHeight: 1.4 }}>
                  Send me SMS updates for new menu launches and exclusive experiences.
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <Btn primary={false} onClick={() => { goPhase(8, 0); }}><ChevronLeft size={15}/> Back</Btn>
              <Btn disabled={!isFormFilled}
                onClick={handleContinueToPayment}
                style={{ flex: 1 }}>Continue to Payment <ChevronRight size={15}/>
              </Btn>
            </div>
          </>}
          {checkoutStep === 2 && (
            <Elements stripe={stripePromise}>
              <PaymentFormInner
                goPhase={goPhase}
                onPaymentComplete={handlePaymentComplete}
                isCompletingPurchase={isProcessingPurchase}
              />
            </Elements>
          )}
          {checkoutStep === 3 && (() => {
            const isCourier = zip && zip.startsWith('9');
            const lockDay = isCourier ? 'Sunday' : 'Friday';
            // const leadDays = isCourier ? 8 : 10;
            const bfastLabel = bundleLabel;
            const topMeals = splitTestMeals.length > 0
              ? splitTestMeals
              : assignedTopMeals;

            const topMealsData = (splitTestData?.getSplitTestOrder || []).slice(0, 3);
            const mealSpecs = (() => {
              const cals = topMealsData.map((m) => m.calories).filter(Boolean);
              const prots = topMealsData.map((m) => m.proteinGrams).filter(Boolean);
              if (!cals.length || !prots.length) return null;
              const calMin = Math.round(Math.min(...cals));
              const calMax = Math.round(Math.max(...cals));
              const protMin = Math.round(Math.min(...prots));
              const protMax = Math.round(Math.max(...prots));
              const protStr = protMin === protMax ? `${protMin}g` : `${protMin}–${protMax}g`;
              const calStr = calMin === calMax ? `${calMin}` : `${calMin}–${calMax}`;
              return `${protStr} protein · ${calStr} cal per meal`;
            })();
            const firstDeliveryDate = effectivePurchase?.deliveryPeriods?.[0]?.startDate;
            const menuWeek = firstDeliveryDate
              ? new Date(`${firstDeliveryDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              : '—';
            const chargedAmount = effectivePurchase?.total
              ? `$${(effectivePurchase.total.amount / 100).toFixed(2)}`
              : null;
            return <>
            <Heading size={26}>You're in, {name || 'there'}. Welcome to Methodology.</Heading>
            {chargedAmount && (
              <div style={{ fontSize: 13, color: C.brown, marginBottom: 4 }}>
                Your card was charged <strong>{chargedAmount}</strong>.
              </div>
            )}
            {currentUser?.email && (
              <div style={{ fontSize: 12, color: C.lightBrown, marginBottom: 20 }}>
                A confirmation email is on its way to {currentUser.email}.
              </div>
            )}

            {/* Welcome video */}
            <div style={{ position: 'relative', background: '#1A1A0F', borderRadius: 0, textAlign: 'center', marginBottom: 24, overflow: 'hidden' }}>
              <video
                src={PosPurchaseVideoURL}
                poster='https://d1cbtwb0qnacuk.cloudfront.net/images/thumbnails/postpurchase-thumbnail.jpeg'
                controls
                playsInline
                style={{ width: '100%', display: 'block' }}
              />
              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ fontFamily: F.h, fontSize: 16, fontWeight: 400, color: C.pureWhite, marginBottom: 4 }}>A message from Stephen</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>30-60s · Kitchen footage, plating, glass jars being packed</div>
              </div>
            </div>

            {/* Personalized summary card */}
            <div style={{ position: 'relative', background: C.darkGreen, borderRadius: 0, padding: '28px 20px 24px', textAlign: 'center', overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 0 }}><Brandmark size={28} color={C.mustard}/></div>
                <div style={{ fontFamily: F.h, fontSize: 20, fontWeight: 400, color: C.pureWhite, marginTop: 12 }}>
                  {effectivePurchase?.bundleName || `Your ${progLabel} Plan · ${sizeLabel}`}
                </div>
                {!effectivePurchase?.bundleName && (
                  <div style={{ fontSize: 12, color: C.lightGreen, marginTop: 6 }}>{daysLabel} · {bfastLabel}</div>
                )}
                {mealSpecs && (
                  <div style={{ fontSize: 12, color: C.lightGreen, marginTop: 2 }}>{mealSpecs}</div>
                )}
                {/* Goals */}
                {userHealthGoals.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: C.mustard, marginBottom: 6 }}>YOUR GOALS</div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {userHealthGoals.slice(0, 4).map((g) => (
                        <Tag key={g.id} color={C.lightGreen} bg="rgba(255,255,255,0.1)">{g.name}</Tag>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top matches */}
                {topMeals.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: C.mustard, marginBottom: 6 }}>YOUR TOP MATCHES</div>
                    <div style={{ fontSize: 12, color: C.lightGreen, lineHeight: 1.6 }}>{topMeals.join(' · ')}</div>
                  </div>
                )}

                {/* Biomarker focus */}
                {listUserBloodworkMarkers.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: C.mustard, marginBottom: 6 }}>BIOMARKER FOCUS</div>
                    <div style={{ fontSize: 12, color: C.lightGreen }}>
                      {listUserBloodworkMarkers.map((m) => `${m.bloodworkMarker.name} (${m.status.toLowerCase().replace('_', ' ')})`).join(' · ')}
                    </div>
                  </div>
                )}

                {/* Share button */}
                {/* <button style={{ marginTop: 18, padding: '8px 24px', background: 'transparent', border: `1px solid ${C.mustard}`, color: C.mustard, fontSize: 11, fontWeight: 700, letterSpacing: '1px', cursor: 'pointer', textTransform: 'uppercase' }}>Share</button> */}
              </div>
            </div>

            {/* Weekly rhythm */}
            <Heading size={18} style={{ marginBottom: 14 }}>One thing to know: your weekly ritual.</Heading>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', rowGap: 14, columnGap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown }}>Monday</div>
              <div style={{ fontSize: 13, color: C.brown, lineHeight: 1.5 }}>New menu drops. We pre-select your meals based on your profile. View your upcoming menu anytime at <span style={{ fontWeight: 600 }}>app.gomethodology.com</span>.</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown }}>Sunday by 11 PM PST</div>
              <div style={{ fontSize: 13, color: C.brown, lineHeight: 1.5 }}>Lock date. Swap anything you want before then. This order is for the Monday that is 8 days away.</div>
              {isCourier && <>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown }}>Monday morning</div>
                <div style={{ fontSize: 13, color: C.brown, lineHeight: 1.5 }}>Meals arrive at your door. Heat, eat, done.</div>
              </>}
            </div>

            {/* Text notification callout */}
            <Callout>
              <div style={{ fontSize: 13, color: C.brown, lineHeight: 1.6 }}>
                We'll text you every Monday when your new menu is ready, and remind you Sunday before it locks. You'll never miss a swap.
              </div>
            </Callout>

            {/* Skip week */}
            <p style={{ fontSize: 11, color: C.lightBrown, margin: '12px 0 28px', lineHeight: 1.5 }}>Want to skip a week? Just hit "Skip" in your account before the lock. No charge, no questions.</p>

            {/* Your first menu */}
            <div style={{ borderTop: '1px solid #E8E4DC', paddingTop: 24, marginBottom: 8 }}>
              <Heading size={18} style={{ marginBottom: 6 }}>Your first menu is ready to review.</Heading>
              <p style={{ fontSize: 13, color: C.brown, lineHeight: 1.6, margin: '0 0 18px' }}>We pre-loaded your top matches for the week of {menuWeek}. Swap anything you'd like before {lockDay}.</p>
              <Btn onClick={() => { goToWeeklyMenu(); scrollTop(); }} style={{ width: '100%' }}>Review My Menu →</Btn>
            </div>

            {/* Referral */}
            <div style={{ borderTop: '1px solid #E8E4DC', marginTop: 28, paddingTop: 28, textAlign: 'center' }}>
              {/* <Heading size={20} style={{ textAlign: 'center', marginBottom: 8 }}>Know someone who'd love this?</Heading>
              <p style={{ fontSize: 13, color: C.brown, lineHeight: 1.6, margin: '0 0 18px' }}>Give them 10% off their first week. You'll get $50 credit when they start.</p>
              <Btn primary={false} style={{ width: '100%', border: `1.5px solid ${C.darkBrown}`, fontWeight: 600 }}>Share Your Link</Btn> */}
              <div style={{ marginTop: 14 }}>
                <a onClick={() => { setMode('home'); scrollTop(); }} style={{ fontSize: 13, color: C.lightBrown, cursor: 'pointer', textDecoration: 'none', borderBottom: '1px solid #E8E4DC', paddingBottom: 1 }}>Return to homepage</a>
              </div>
            </div>
          </>;
          })()}
        </Fade>
      </div>
      {backgroundJobId && (
        <BackgroundJobStatusCheck
          backgroundJobId={backgroundJobId}
          onSuccess={handlePurchaseSuccess}
          onError={handlePurchaseError}
        />
      )}
    </div>
  );
};

const CustomFormGroup = styled(FormGroup)`
  margin-bottom: 1.5em;
`;

const CustomCheckbox = styled(Checkbox)`
  font-size: ${(p) => p.theme.typography.sizes.small};
  .checkbox {
    margin-right: 1em;
    font-size: ${(p) => p.theme.typography.sizes.big};
    align-item: top;
  }
`;

const InnerLink = styled.a`
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default Phase8Checkout;
